import { supabase } from './supabase'

// Admin service - uses the main supabase client with proper authentication
export class AdminService {
  
  // Update user status using auth metadata only (no table dependencies)
  static async updateUserStatus(userId, status) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`Admin: Updating user status to ${status}`)
      }
      
      // Call the backend admin API
      const response = await fetch(`http://localhost:3001/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: status })
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        console.debug('Backend update status API error:', response.status, errorText)
        return { success: false, error: `Backend API error: ${errorText}` }
      }
      
      const result = await response.json()
      if (result.success) {
        return { success: true, data: result.data }
      } else {
        console.debug('Backend API returned error:', result.error)
        return { success: false, error: result.error }
      }
      
    } catch (error) {
      console.debug('Error in updateUserStatus:', error)
      return { success: false, error: error.message || 'Unknown error occurred' }
    }
  }
  
  // Delete user from auth system (no table dependencies)
  static async deleteUser(userId) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Admin: Deleting user via backend API')
      }
      
      // Call the backend admin API
      const response = await fetch(`http://localhost:3001/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        console.debug('Backend delete user API error:', response.status, errorText)
        return { success: false, error: `Backend API error: ${errorText}` }
      }
      
      const result = await response.json()
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        console.debug('Backend API returned error:', result.error)
        return { success: false, error: result.error }
      }
      
    } catch (error) {
      console.debug('Admin delete user failed:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Update user metadata
  static async updateUserMetadata(userId, metadata) {
    try {
      console.log(`AdminService: Updating user ${userId} metadata:`, metadata)
      
      const { data, error } = await supabase.auth.admin.updateUserById(
        userId,
        { user_metadata: metadata }
      )
      
      if (error) {
        console.error('Error updating user metadata:', error)
        return { success: false, error: error.message || 'Failed to update user metadata' }
      }
      
      console.log(`✅ User ${userId} metadata updated successfully:`, data.user?.user_metadata)
      return { success: true, data }
    } catch (error) {
      console.error('Error in updateUserMetadata:', error)
      return { success: false, error: error.message || 'Unknown error updating metadata' }
    }
  }
  
  // Get all users from database
  static async getAllUsers() {
    try {
      // Call the backend admin API instead of direct Supabase calls
      const response = await fetch('http://localhost:3001/api/admin/users', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        console.debug('Backend admin API error:', response.status, errorText)
        
        // Fallback to current user only
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          return { 
            success: true, 
            users: [{
              id: user.id,
              email: user.email,
              fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Current User',
              role: user.user_metadata?.role || 'user',
              status: user.user_metadata?.status || 'approved',
              isAdmin: await this.isAdmin(user),
              emailVerified: user.email_confirmed_at ? true : false,
              createdAt: user.created_at,
              lastSignIn: user.last_sign_in_at
            }]
          }
        }
        return { success: false, error: 'Backend API unavailable and no current user' }
      }
      
      const result = await response.json()
      if (result.success) {
        // Only log user count in development mode
        if (process.env.NODE_ENV === 'development') {
          console.debug(`Admin: Loaded ${result.users.length} users`)
        }
        return { success: true, users: result.users }
      } else {
        console.debug('Backend API returned error:', result.error)
        return { success: false, error: result.error }
      }
      
    } catch (error) {
      console.debug('Error in getAllUsers:', error)
      return { success: false, error: error.message }
    }
  }

  // Get user status from auth metadata only (no table dependencies)
  static async getUserStatus(userId) {
    try {
      console.log(`Getting user status for ${userId} from auth metadata...`)
      
      // Get user data from auth system directly
      const { data: authData, error: authError } = await supabase.auth.admin.getUserById(userId)
      
      if (authError) {
        console.error('Error getting user from auth:', authError)
        return { success: false, error: authError.message }
      }
      
      const status = authData.user?.user_metadata?.status || 'approved' // Default to approved
      console.log(`✅ User ${userId} status: ${status}`)
      
      return { success: true, status }
    } catch (error) {
      console.error('Error in getUserStatus:', error)
      return { success: false, error: error.message }
    }
  }
  
  // Check if current user is admin using multiple methods
  static async isAdmin(user) {
    if (!user?.id) return false
    
    try {
      // Method 1: Check user metadata (primary method - your existing data)
      if (user.user_metadata?.role === 'admin' || 
          user.user_metadata?.role === 'super_admin' ||
          user.user_metadata?.admin_level === 'super_admin' ||
          user.user_metadata?.is_admin === true) {
        return true
      }
      
      // Method 2: Check app metadata
      if (user.app_metadata?.role === 'admin' || user.app_metadata?.role === 'super_admin') {
        return true
      }
      
      // Method 3: Legacy email check (for your admin users)
      if (user?.email === 'nisjet.lapi@gmail.com') {
        return true
      }
      
      return false
    } catch (error) {
      console.debug('Error checking admin status:', error)
      return user?.email === 'nisjet.lapi@gmail.com'
    }
  }
  
  // Get admin role for user (metadata only, no table dependencies)
  static async getAdminRole(user) {
    if (!user?.id) {
      return 'user'
    }
    
    try {
      // Check user metadata first (primary source)
      if (user.user_metadata?.role) {
        const role = user.user_metadata.role
        if (['admin', 'super_admin', 'moderator'].includes(role)) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('Found role in user_metadata:', role)
          }
          return role
        }
      }
      
      // Check app metadata
      if (user.app_metadata?.role) {
        const role = user.app_metadata.role
        if (['admin', 'super_admin', 'moderator'].includes(role)) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('Found role in app_metadata:', role)
          }
          return role
        }
      }
      
      // Legacy email check
      if (user?.email === 'nisjet.lapi@gmail.com') {
        if (process.env.NODE_ENV === 'development') {
          console.debug('Legacy admin check passed, returning super_admin')
        }
        return 'super_admin'
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('No admin role found, returning user')
      }
      return 'user'
    } catch (error) {
      console.debug('Error getting admin role:', error)
      const fallbackRole = user?.email === 'nisjet.lapi@gmail.com' ? 'super_admin' : 'user'
      if (process.env.NODE_ENV === 'development') {
        console.debug('Error fallback, returning:', fallbackRole)
      }
      return fallbackRole
    }
  }
}

export default AdminService
